import { useMemo, useRef, useState } from "react";
import { Plus, Trash2, UserPlus } from "lucide-react";
import type { Attendee } from "../types";
import { useAccountContacts } from "../hooks/useApi";
import { cn } from "../lib/utils";
import styles from "./AttendeeEditor.module.css";

interface AttendeeEditorProps {
  attendees: Attendee[];
  onChange: (attendees: Attendee[]) => void;
  accountUuid?: string | null;
}

/**
 * Editable attendee list with add/remove and swipe-to-delete.
 * Each row shows name + role, with inline editing.
 * If accountUuid is provided, shows suggestion chips for known contacts.
 */
export function AttendeeEditor({ attendees, onChange, accountUuid }: AttendeeEditorProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("");
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Track swipe state per attendee index
  const [swipeIdx, setSwipeIdx] = useState<number | null>(null);
  const touchStartX = useRef(0);

  function handleAdd() {
    const trimmed = newName.trim();
    if (!trimmed) return;
    onChange([
      ...attendees,
      { name: trimmed, role: newRole.trim() || null, is_new_contact: false },
    ]);
    setNewName("");
    setNewRole("");
    setShowAddForm(false);
  }

  function handleRemove(idx: number) {
    onChange(attendees.filter((_, i) => i !== idx));
    setSwipeIdx(null);
  }

  function handleNameChange(idx: number, value: string) {
    const updated = attendees.map((a, i) =>
      i === idx ? { ...a, name: value } : a
    );
    onChange(updated);
  }

  function handleRoleChange(idx: number, value: string) {
    const updated = attendees.map((a, i) =>
      i === idx ? { ...a, role: value || null } : a
    );
    onChange(updated);
  }

  function handleTouchStart(idx: number, e: React.TouchEvent) {
    touchStartX.current = e.touches[0].clientX;
    // Reset other open swipes
    if (swipeIdx !== null && swipeIdx !== idx) {
      setSwipeIdx(null);
    }
  }

  function handleTouchEnd(idx: number, e: React.TouchEvent) {
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    // Swipe left > 60px reveals delete
    if (deltaX < -60) {
      setSwipeIdx(idx);
    } else if (deltaX > 40) {
      setSwipeIdx(null);
    }
  }

  // Known contacts from account history
  const { contacts: knownContacts } =
    useAccountContacts(accountUuid ?? null);

  // Filter out contacts that are already added as attendees
  const suggestions = useMemo(() => {
    if (!knownContacts.length) return [];
    const addedNames = new Set(
      attendees.map((a) => a.name.toLowerCase().trim())
    );
    return knownContacts.filter(
      (c) => !addedNames.has(c.name.toLowerCase().trim())
    );
  }, [knownContacts, attendees]);

  function handleAddSuggestion(name: string) {
    onChange([
      ...attendees,
      { name, role: null, is_new_contact: false },
    ]);
  }

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <h3 className={styles.label}>Attendees</h3>
        <button
          type="button"
          className={styles.addButton}
          onClick={() => {
            setShowAddForm(true);
            requestAnimationFrame(() => nameInputRef.current?.focus());
          }}
          aria-label="Add attendee"
        >
          <UserPlus className="h-4 w-4" />
          <span>Add</span>
        </button>
      </div>

      {suggestions.length > 0 && (
        <div className={styles.suggestions}>
          <span className={styles.suggestionsLabel}>Known contacts:</span>
          <div className={styles.chipList}>
            {suggestions.map((c) => (
              <button
                key={c.name}
                type="button"
                className={styles.chip}
                onClick={() => handleAddSuggestion(c.name)}
                aria-label={`Add ${c.name}`}
              >
                {c.name}
                <span className={styles.chipFreq}>({c.frequency}x)</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {attendees.length === 0 && !showAddForm && suggestions.length === 0 && (
        <p className={styles.empty}>No attendees. Tap "Add" to add one.</p>
      )}

      <ul className={styles.list}>
        {attendees.map((att, idx) => (
          <li
            key={idx}
            className={cn(styles.row, swipeIdx === idx && styles.rowSwiped)}
            onTouchStart={(e) => handleTouchStart(idx, e)}
            onTouchEnd={(e) => handleTouchEnd(idx, e)}
          >
            <div className={styles.rowContent}>
              <input
                type="text"
                className={styles.nameInput}
                value={att.name}
                onChange={(e) => handleNameChange(idx, e.target.value)}
                placeholder="Name"
                aria-label="Attendee name"
              />
              <input
                type="text"
                className={styles.roleInput}
                value={att.role ?? ""}
                onChange={(e) => handleRoleChange(idx, e.target.value)}
                placeholder="Role (optional)"
                aria-label="Attendee role"
              />
            </div>
            <button
              type="button"
              className={cn(
                styles.deleteButton,
                swipeIdx === idx && styles.deleteVisible
              )}
              onClick={() => handleRemove(idx)}
              aria-label={`Remove ${att.name}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>

      {showAddForm && (
        <div className={styles.addForm}>
          <input
            ref={nameInputRef}
            type="text"
            className={styles.nameInput}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Name"
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            aria-label="New attendee name"
          />
          <input
            type="text"
            className={styles.roleInput}
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            placeholder="Role (optional)"
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            aria-label="New attendee role"
          />
          <div className={styles.addFormActions}>
            <button
              type="button"
              className={styles.confirmAdd}
              onClick={handleAdd}
            >
              <Plus className="h-4 w-4" />
              Add
            </button>
            <button
              type="button"
              className={styles.cancelAdd}
              onClick={() => {
                setShowAddForm(false);
                setNewName("");
                setNewRole("");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
